
import { useState } from 'react';

interface FAQSectionProps {
  language: 'es' | 'en';
}

export default function FAQSection({ language }: FAQSectionProps) {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const content = {
    es: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Resolvemos tus dudas más comunes',
      faqs: [
        {
          question: '¿Cuánto tarda el registro?',
          answer: 'Con nuestro acompañamiento, todos los trámites municipales se completan en 1 día. Sin nosotros, puede tomar semanas debido a las barreras del idioma y la complejidad del sistema.'
        },
        {
          question: '¿Necesito hablar japonés?',
          answer: 'No, nosotros te acompañamos y traducimos todo. Juampi y Kisara te asistirán — Juampi habla español e inglés, y Kisara es hablante nativa de japonés, por lo que no tendrás ningún problema de comunicación.'
        },
        {
          question: '¿Dónde están ubicados?',
          answer: 'Estamos basados en Osaka, pero también atendemos en Tokio y otras ciudades principales. Para el Premium Pack, ofrecemos asistencia en aeropuertos Kansai, Haneda y Narita.'
        },
        {
          question: '¿Puedo pagar desde el exterior?',
          answer: 'Sí, aceptamos PayPal y Wise para pagos internacionales. También aceptamos transferencias bancarias y pagos en efectivo una vez en Japón.'
        },
        {
          question: '¿Qué incluye exactamente el acompañamiento?',
          answer: 'Te acompañamos físicamente a todas las oficinas, traducimos en tiempo real, llenamos formularios contigo, y nos aseguramos de que entiendas cada paso del proceso.'
        },
        {
          question: '¿Cuándo debo contactarlos?',
          answer: 'Idealmente, 1-2 semanas antes de tu llegada a Japón para el Premium Pack, o dentro de los primeros días de llegada para otros paquetes. Mientras más temprano, mejor podemos preparar todo.'
        }
      ]
    },
    en: {
      title: 'Frequently Asked Questions',
      subtitle: 'We solve your most common doubts',
      faqs: [
        {
          question: 'How long does registration take?',
          answer: 'With our support, all municipal procedures are completed in 1 day. Without us, it can take weeks due to language barriers and system complexity.'
        },
        {
          question: 'Do I need to speak Japanese?',
          answer: 'No, we accompany you and translate everything. Juampi and Kisara will assist you — Juampi speaks Spanish and English, and Kisara is a native Japanese speaker, so you won\'t have any communication issues.'
        },
        {
          question: 'Where are you located?',
          answer: 'We are based in Osaka, but we also serve Tokyo and other major cities. For the Premium Pack, we offer assistance at Kansai, Haneda and Narita airports.'
        },
        {
          question: 'Can I pay from abroad?',
          answer: 'Yes, we accept PayPal and Wise for international payments. We also accept bank transfers and cash payments once in Japan.'
        },
        {
          question: 'What exactly does the support include?',
          answer: 'We physically accompany you to all offices, translate in real time, fill out forms with you, and make sure you understand every step of the process.'
        },
        {
          question: 'When should I contact you?',
          answer: 'Ideally, 1-2 weeks before your arrival in Japan for the Premium Pack, or within the first days of arrival for other packages. The earlier, the better we can prepare everything.'
        }
      ]
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4">
              {content[language].title} ❓
            </h2>
            <p className="text-xl text-gray-600">
              {content[language].subtitle}
            </p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {content[language].faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-red-50 rounded-xl border border-red-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-red-100 transition-colors cursor-pointer"
                >
                  <h3 className="text-lg font-semibold text-red-600 pr-4">
                    {faq.question}
                  </h3>
                  <i className={`ri-${openFAQ === index ? 'subtract' : 'add'}-line text-red-600 text-xl flex-shrink-0`}></i>
                </button>
                
                {openFAQ === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              {language === 'es' 
                ? '¿Tienes más preguntas? ¡Estamos aquí para ayudarte!'
                : 'Have more questions? We\'re here to help!'
              }
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              {language === 'es' ? 'Contactar ahora' : 'Contact now'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
